import { IconCheck } from "@tabler/icons-react";
import { motion, useReducedMotion } from "motion/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useModalKeyboard } from "../../components/app/DesktopDialogOverlay";
import { useMountTime } from "../../hooks/useMountTime";
import { cn } from "../../lib/utils";
import { WIZARD_DARK_SPRING, WIZARD_GLIDE } from "./wizardMotion";

/**
 * The room sheet of gate R (Figma R1 and D-R1, docs/juiscigpt-wizard.md
 * REQ-02): the room member learns that JuisciGPT answers clinical questions,
 * and where the room stays. A white sheet at the bottom of the blurred
 * JuisciGPT home on mobile, a dialog in the middle of the content area on
 * desktop. Escape and a tap outside the sheet are "Plus tard" (REQ-11). The
 * focus enters the sheet and Tab stays inside it (INV-02).
 */
export function WizardRoomSheet({
  roomName,
  layout,
  onStart,
  onLater,
}: {
  roomName: string;
  layout: "mobile" | "desktop";
  onStart: (secondsShown: number) => void;
  onLater: (secondsShown: number) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion() === true;
  // The seconds since the card showed, for the analytics of the choice (REQ-30).
  const shownAt = useMountTime();
  const secondsShown = (): number =>
    Math.round((performance.now() - shownAt) / 100) / 10;
  const later = (): void => {
    onLater(secondsShown());
  };
  const cardRef = useRef<HTMLDivElement>(null);
  useModalKeyboard({ cardRef, onClose: later, enabled: true });
  return (
    <motion.div
      className={cn(
        "fixed inset-0 z-50 flex justify-center bg-ds-moss-600/30 backdrop-blur-sm",
        layout === "desktop" ? "items-center px-3" : "items-end"
      )}
      style={{ paddingTop: "calc(var(--safe-area-top) + 1rem)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={reducedMotion ? { duration: 0 } : WIZARD_GLIDE}
      onClick={later}
    >
      <motion.div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="juiscigpt-wizard-room-title"
        className={cn(
          "flex max-h-full w-full flex-col gap-3 overflow-y-auto overscroll-contain bg-ds-white px-6 text-ds-slate-900 shadow-2xl",
          layout === "desktop"
            ? "max-w-md rounded-3xl pt-6"
            : "max-w-lg rounded-t-4xl pt-3"
        )}
        style={{
          paddingBottom:
            layout === "desktop"
              ? "1.5rem"
              : "calc(var(--safe-area-bottom) + 1.25rem)",
        }}
        initial={reducedMotion ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reducedMotion ? { duration: 0 } : WIZARD_DARK_SPRING}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {layout === "mobile" ? (
          <span
            aria-hidden="true"
            className="mx-auto mb-3 h-1 w-10 rounded-full bg-ds-slate-200"
          />
        ) : null}
        <p className="flex items-center gap-1.5 text-sm font-medium text-ds-mint-700">
          <IconCheck className="size-4 shrink-0" stroke={2} />
          {t("You joined the {{room}} room", { room: roomName })}
        </p>
        <h2
          id="juiscigpt-wizard-room-title"
          className="text-2xl font-semibold leading-tight text-balance"
        >
          {t("Ask your clinical questions to JuisciGPT now")}
        </h2>
        <p className="text-base leading-snug text-ds-slate-600">
          {layout === "desktop"
            ? t(
                "JuisciGPT becomes your home page: an AI that answers and cites its sources. Your room stays in Rooms, in the left bar."
              )
            : t(
                "JuisciGPT becomes your home page: an AI that answers and cites its sources. Your room stays in the Rooms tab."
              )}
        </p>
        <div className="flex flex-col gap-1 pt-2">
          <button
            type="button"
            className="h-14 w-full rounded-xl bg-ds-mint-800 text-base font-medium text-ds-white"
            onClick={() => {
              onStart(secondsShown());
            }}
          >
            {t("Start the tour")}
          </button>
          <button
            type="button"
            className="h-14 w-full text-base font-medium text-ds-slate-900"
            onClick={later}
          >
            {t("Later")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
